import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton, Chip,
  TextField, InputAdornment, Avatar, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, MenuItem, CircularProgress, Tooltip, Alert,
  LinearProgress, Divider, List, ListItem, ListItemText
} from '@mui/material';
import {
  Add, Edit, Delete, Search, Upload as UploadIcon, Download as DownloadIcon,
  CheckCircle, Warning
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { studentsAPI, settingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const GENDER = ['male', 'female', 'other'];
const STATUS = ['active', 'inactive', 'graduated', 'transferred', 'expelled'];
const BLOOD  = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];

const EMPTY = {
  first_name:'', last_name:'', other_names:'', gender:'male', date_of_birth:'',
  admission_number:'', admission_date:'', phone:'', email:'', address:'',
  parent_name:'', parent_phone:'', parent_email:'', parent_relationship:'',
  blood_group:'', medical_notes:'', status:'active'
};

// ── Bulk Import Dialog ────────────────────────────────────────────────────────
function BulkImportDialog({ open, onClose, onDone, academicYears, classes }) {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState([]);
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [autoEnrol, setAutoEnrol] = useState(true);
  const [parseError, setParseError] = useState('');

  const parseCSV = async (f) => {
    setParseError(''); setResult(null); setRows([]); setPreview([]);
    const text = await f.text();
    const lines = text.split('\n').filter(l => !l.startsWith('#') && l.trim());
    if (lines.length < 2) { setParseError('File is empty or has no data rows'); return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const parsed = lines.slice(1).map(line => {
      const vals = line.split(',');
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i]?.trim() || ''; });
      return obj;
    }).filter(r => r.first_name || r.admission_number);
    setRows(parsed);
    setPreview(parsed.slice(0, 5));
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    parseCSV(f);
  };

  const handleImport = async () => {
    if (!rows.length) return enqueueSnackbar('No valid rows to import', { variant: 'warning' });
    setImporting(true);
    try {
      const res = await axios.post(`${API}/import/students`, {
        rows, academic_year_id: selectedYear || null, auto_enrol: autoEnrol
      });
      if (res.data.success) {
        setResult(res.data);
        onDone();
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Import failed', { variant: 'error' });
    } finally { setImporting(false); }
  };

  const handleDownloadTemplate = () => {
    axios.get(`${API}/import/template/students`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a'); a.href = url;
        a.download = 'students_import_template.csv'; a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => enqueueSnackbar('Error downloading template', { variant: 'error' }));
  };

  const handleClose = () => {
    setFile(null); setRows([]); setPreview([]); setResult(null); setParseError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Import Students</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Download the CSV template, fill it in, then upload. Column headers must match exactly.
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
              Download CSV Template
            </Button>
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          <Grid item xs={12} sm={6}>
            <TextField select label="Academic Year (for auto-enrolment)" value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}>
              <MenuItem value="">None — don't auto-enrol</MenuItem>
              {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term} (Current)</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant={autoEnrol ? 'contained' : 'outlined'} size="small"
              onClick={() => setAutoEnrol(!autoEnrol)} color="primary">
              {autoEnrol ? '✓ Auto-enrol into class' : 'Skip auto-enrolment'}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" component="label" startIcon={<UploadIcon />} color="secondary">
              Select CSV File
              <input type="file" accept=".csv" hidden onChange={handleFileChange} />
            </Button>
            {file && <Typography variant="caption" sx={{ ml: 2 }}>{file.name} — {rows.length} rows detected</Typography>}
          </Grid>

          {parseError && <Grid item xs={12}><Alert severity="error">{parseError}</Alert></Grid>}

          {preview.length > 0 && !result && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Preview (first 5 rows):</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      {Object.keys(preview[0]).slice(0,7).map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.map((row, i) => (
                      <TableRow key={i}>
                        {Object.keys(preview[0]).slice(0,7).map(h => (
                          <TableCell key={h} sx={{ fontSize: 11 }}>{row[h] || '—'}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Grid>
          )}

          {result && (
            <Grid item xs={12}>
              <Alert severity={result.skipped > 0 ? 'warning' : 'success'} icon={<CheckCircle />}>
                <Typography fontWeight={700}>{result.message}</Typography>
                <Typography variant="caption">
                  {result.imported} imported · {result.skipped} skipped
                </Typography>
              </Alert>
              {result.errors?.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 150, overflowY: 'auto' }}>
                  {result.errors.map((e, i) => (
                    <Typography key={i} variant="caption" color="error" display="block">
                      <Warning fontSize="inherit" sx={{ mr: 0.5 }} />{e}
                    </Typography>
                  ))}
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {!result && (
          <Button variant="contained" onClick={handleImport}
            disabled={importing || !rows.length}>
            {importing ? <CircularProgress size={20} /> : `Import ${rows.length} Students`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Main Students Page ────────────────────────────────────────────────────────
export default function Students() {
  const { enqueueSnackbar } = useSnackbar();
  const { can } = useAuth();
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await studentsAPI.getAll({ search, page: page + 1, limit: rowsPerPage });
      if (res.data.success) { setStudents(res.data.data); setTotal(res.data.total); }
    } finally { setLoading(false); }
  }, [search, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAcademicYears(r.data.data); }).catch(() => {});
  }, []);

  const openCreate = async () => {
    setEditing(null);
    try {
      const res = await studentsAPI.getNextAdmission();
      setForm({ ...EMPTY, admission_number: res.data.data || '' });
    } catch { setForm(EMPTY); }
    setPhoto(null); setDialogOpen(true);
  };

  const openEdit = (student) => {
    setEditing(student);
    setForm({
      first_name: student.first_name || '', last_name: student.last_name || '',
      other_names: student.other_names || '', gender: student.gender || 'male',
      date_of_birth: student.date_of_birth?.split('T')[0] || '',
      admission_number: student.admission_number || '',
      admission_date: student.admission_date?.split('T')[0] || '',
      phone: student.phone || '', email: student.email || '',
      address: student.address || '', parent_name: student.parent_name || '',
      parent_phone: student.parent_phone || '', parent_email: student.parent_email || '',
      parent_relationship: student.parent_relationship || '',
      blood_group: student.blood_group || '', medical_notes: student.medical_notes || '',
      status: student.status || 'active'
    });
    setPhoto(null); setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.admission_number) {
      return enqueueSnackbar('First name, last name and admission number are required', { variant: 'warning' });
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.keys(form).forEach(k => fd.append(k, form[k]));
      if (photo) fd.append('profile_photo', photo);
      if (editing) await studentsAPI.update(editing.id, fd);
      else await studentsAPI.create(fd);
      enqueueSnackbar(editing ? 'Student updated' : 'Student created', { variant: 'success' });
      setDialogOpen(false); load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error saving student', { variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this student? This cannot be undone.')) return;
    try {
      await studentsAPI.delete(id);
      enqueueSnackbar('Student deleted', { variant: 'success' });
      load();
    } catch { enqueueSnackbar('Error deleting student', { variant: 'error' }); }
  };

  const getPhotoUrl = (f) => f ? `${process.env.REACT_APP_API_URL?.replace('/api','')}/uploads/${f}` : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Students Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {can('students', 'full') && (
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>
              Bulk Import
            </Button>
          )}
          {can('students', 'full') && (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Student</Button>
          )}
        </Box>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <TextField placeholder="Search by name, admission number..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ maxWidth: 400 }} />
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Photo</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Admission No.</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Parent</TableCell>
                <TableCell>Status</TableCell>
                {can('students', 'full') && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
                : students.length === 0
                ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>No students found</TableCell></TableRow>
                : students.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Avatar src={getPhotoUrl(s.profile_photo)} sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 13 }}>
                        {s.first_name?.[0]}{s.last_name?.[0]}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} fontSize={14}>{s.first_name} {s.last_name}</Typography>
                      {s.other_names && <Typography variant="caption" color="text.secondary">{s.other_names}</Typography>}
                    </TableCell>
                    <TableCell><Chip label={s.admission_number} size="small" variant="outlined" /></TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{s.gender}</TableCell>
                    <TableCell>{s.class_name ? `${s.class_name}${s.stream ? ' '+s.stream : ''}` : '—'}</TableCell>
                    <TableCell>
                      <Typography fontSize={13}>{s.parent_name || '—'}</Typography>
                      {s.parent_phone && <Typography variant="caption" color="text.secondary">{s.parent_phone}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={s.status} size="small"
                        color={s.status === 'active' ? 'success' : s.status === 'graduated' ? 'info' : 'default'} />
                    </TableCell>
                    {can('students', 'full') && (
                      <TableCell align="right">
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(s)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(s.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]} />
      </Card>

      {/* ── Bulk Import Dialog ── */}
      <BulkImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => { load(); }}
        academicYears={academicYears}
        classes={classes}
      />

      {/* ── Student Form Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Student' : 'Add Student'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={4}><TextField label="First Name *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Last Name *" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Other Names" value={form.other_names} onChange={e => setForm({ ...form, other_names: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Gender" value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                {GENDER.map(g => <MenuItem key={g} value={g} sx={{ textTransform: 'capitalize' }}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}><TextField label="Date of Birth" type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Admission Number *" value={form.admission_number} onChange={e => setForm({ ...form, admission_number: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Admission Date" type="date" value={form.admission_date} onChange={e => setForm({ ...form, admission_date: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField label="Address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} multiline rows={2} /></Grid>
            <Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">Parent / Guardian</Typography></Grid>
            <Grid item xs={12} sm={4}><TextField label="Parent Name" value={form.parent_name} onChange={e => setForm({ ...form, parent_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Parent Phone" value={form.parent_phone} onChange={e => setForm({ ...form, parent_phone: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Relationship" value={form.parent_relationship} onChange={e => setForm({ ...form, parent_relationship: e.target.value })} /></Grid>
            <Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">Medical & Other</Typography></Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Blood Group" value={form.blood_group} onChange={e => setForm({ ...form, blood_group: e.target.value })}>
                <MenuItem value="">Not specified</MenuItem>
                {BLOOD.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {STATUS.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="outlined" component="label" fullWidth sx={{ height: 40 }}>
                {photo ? photo.name : 'Upload Photo'}
                <input type="file" accept="image/*" hidden onChange={e => setPhoto(e.target.files[0])} />
              </Button>
            </Grid>
            <Grid item xs={12}><TextField label="Medical Notes" value={form.medical_notes} onChange={e => setForm({ ...form, medical_notes: e.target.value })} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
